/**
 * Outage back-entry: typing in a sale that was made on paper while CenoX was
 * unreachable.
 *
 * The screen asks for what is on the slip — its reference, when the sale
 * happened, who was at the counter, the items with their batch, and how it was
 * paid. When it was typed in, and by whom, are not asked: the server records
 * them. A slip is entered once per location; a second attempt is refused.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type PaymentMethod } from '@cenox/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { dateTime, fromDateTimeLocal, money } from '../../lib/format';
import { Button, Card, CardHeader, EmptyState, Input, Select, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type BatchRow, type ListOf, type MedicineListItem } from './data';
import { ErrorLine, StatusBadge, Table, Td } from './parts';
import { LocationPicker, ProductPicker, useActionBranch } from './MedicinesPage';
import { needsTransferReference, transferReferenceReady } from '../../lib/till';

interface Line {
  key: string;
  product: { id: string; name: string } | null;
  unitId: string;
  quantity: string;
  batchId: string;
}

interface Tender {
  key: string;
  method: PaymentMethod;
  amount: string;
  reference: string;
  confirmed: boolean;
}

interface Member {
  userId: string | null;
  fullName: string | null;
  status: string;
  role: string;
}

interface EntryRow {
  id: string;
  branch_name: string;
  outage_log_ref: string;
  original_occurred_at: string;
  entered_at: string;
  entered_by_name: string;
  original_staff_name: string;
  reference: string;
  amount: string;
  payment_status: string;
}

const blankLine = (): Line => ({ key: newKey(), product: null, unitId: '', quantity: '1', batchId: '' });
const blankTender = (): Tender => ({ key: newKey(), method: 'cash', amount: '', reference: '', confirmed: false });

export function OutageEntryPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const c = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'Africa/Lagos';
  const branch = useActionBranch();
  const [slip, setSlip] = useState('');
  const [when, setWhen] = useState('');
  const [staffUserId, setStaffUserId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  const [tenders, setTenders] = useState<Tender[]>([blankTender()]);
  const [note, setNote] = useState('');
  // One key per slip being entered: a retry after a network failure reuses it.
  const [key, setKey] = useState(newKey);
  const enter = usePharmacyWrite<Record<string, unknown>, { reference: string; amount: string; paymentStatus: string; pendingAmount: string; duplicate: boolean; enteredAt: string }>('post', '/outage-entries');
  const entries = usePharmacy<ListOf<EntryRow>>('/outage-entries', { branchId: branch.branchId || undefined }, can('pharmacy:outage_entry'));
  const members = useQuery({
    queryKey: ['members', path('')],
    queryFn: () => api.get<{ data: Member[] }>(path('/members')),
  });
  const people = (members.data?.data ?? []).filter((m) => m.userId && m.status === 'active' && m.role !== 'location_operator');

  if (!can('pharmacy:outage_entry')) {
    return (
      <div className="space-y-5">
        <PageHeader title="Outage back-entry" />
        <EmptyState title="Not available to you" description="Entering outage sales is a manager's job. Ask the owner." />
      </div>
    );
  }

  function reset() {
    setSlip('');
    setWhen('');
    setLines([blankLine()]);
    setTenders([blankTender()]);
    setNote('');
    setKey(newKey());
    enter.reset();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const picked = people.find((p) => p.userId === staffUserId);
    enter.mutate({
      branchId: branch.branchId,
      outageLogRef: slip.trim(),
      originalOccurredAt: fromDateTimeLocal(when, tz),
      originalStaffUserId: staffUserId || null,
      originalStaffName: picked?.fullName ?? staffName.trim(),
      lines: lines
        .filter((l) => l.unitId)
        .map((l) => ({ unitId: l.unitId, quantity: Number.parseInt(l.quantity, 10), ...(l.batchId ? { batchId: l.batchId } : {}) })),
      tenders: tenders
        .filter((t) => t.amount.trim())
        .map((t) => ({ method: t.method, amount: t.amount, reference: t.reference.trim() || null, confirmed: t.method === 'cash' || t.confirmed })),
      note: note.trim() || null,
      idempotencyKey: key,
    });
  }

  const ready =
    branch.branchId && slip.trim() && when && (staffUserId || staffName.trim()) &&
    lines.some((l) => l.unitId && /^\d+$/.test(l.quantity) && Number(l.quantity) > 0) &&
    // A confirmed transfer is believed on its alert's reference, which pays once.
    !tenders.some((t) => t.amount.trim() && t.confirmed && needsTransferReference(t.method) && !transferReferenceReady(t.reference));

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/pos" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> POS
      </Link>
      <PageHeader
        title="Outage back-entry"
        description="Enter a sale from the paper outage log, as it happened. When and by whom it is entered is recorded automatically."
      />

      {enter.data ? (
        <Card>
          <CardHeader title={enter.data.duplicate ? 'Already entered' : 'Entered'} />
          <p className="text-sm text-slate-700">
            Slip <span className="font-semibold">{slip}</span> is sale <span className="font-semibold">{enter.data.reference}</span> for{' '}
            {money(enter.data.amount, c)}, dated when it happened. Entered {dateTime(enter.data.enteredAt, tz)}.
          </p>
          {Number(enter.data.pendingAmount) > 0 && (
            <p className="mt-2 text-sm text-amber-700">
              {money(enter.data.pendingAmount, c)} by card or transfer is pending until it is matched to the slip or bank alert
              (Finance → Sales).
            </p>
          )}
          <Button className="mt-4" onClick={reset}>Enter the next slip</Button>
        </Card>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <Card>
            <CardHeader title="The slip" subtitle="Exactly as written on the paper outage log" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <LocationPicker value={branch.branchId} onChange={branch.setBranchId} options={branch.options} />
              <Input label="Slip reference" required value={slip} onChange={(e) => setSlip(e.target.value)} placeholder="OUT-0007" />
              <Input label="When it happened" required type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} hint={`Pharmacy time (${tz})`} />
              <Select
                label="Who sold it"
                value={staffUserId}
                placeholder="Choose, or type a name below"
                onChange={(e) => setStaffUserId(e.target.value)}
                options={people.map((p) => ({ value: p.userId!, label: p.fullName ?? 'Member' }))}
              />
              {!staffUserId && (
                <Input label="Name, if not a member" value={staffName} onChange={(e) => setStaffName(e.target.value)} hint="Prescription items need a member who may dispense" />
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Items" subtitle="Whole units. Name the batch from the slip; leave it empty for earliest expiry as it stood then." />
            <div className="space-y-3">
              {lines.map((line, i) => (
                <LineEditor
                  key={line.key}
                  line={line}
                  branchId={branch.branchId}
                  onChange={(next) => setLines(lines.map((l, j) => (j === i ? next : l)))}
                  onRemove={lines.length > 1 ? () => setLines(lines.filter((_, j) => j !== i)) : undefined}
                />
              ))}
              <Button type="button" variant="secondary" size="sm" icon={<Plus className="size-4" />} onClick={() => setLines([...lines, blankLine()])}>
                Add item
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader title="Payment" subtitle="Cash as counted. Card or transfer needs the slip or alert reference, and stays pending unless you have matched it." />
            <div className="space-y-3">
              {tenders.map((t, i) => (
                <div key={t.key} className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:items-end">
                  <Select
                    label="Method"
                    value={t.method}
                    onChange={(e) => setTenders(tenders.map((x, j) => (j === i ? { ...x, method: e.target.value as PaymentMethod } : x)))}
                    options={PAYMENT_METHODS.filter((m) => m !== 'credit').map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
                  />
                  <Input label="Amount" inputMode="decimal" value={t.amount} onChange={(e) => setTenders(tenders.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
                  <Input
                    label={t.method === 'cash' ? 'Reference (optional)' : 'Slip / alert reference'}
                    required={t.method !== 'cash'}
                    value={t.reference}
                    onChange={(e) => setTenders(tenders.map((x, j) => (j === i ? { ...x, reference: e.target.value } : x)))}
                  />
                  {t.method !== 'cash' ? (
                    <label className="flex items-center gap-2 text-sm text-slate-700 sm:pb-2">
                      <input type="checkbox" checked={t.confirmed} onChange={(e) => setTenders(tenders.map((x, j) => (j === i ? { ...x, confirmed: e.target.checked } : x)))} />
                      Matched to slip / alert
                    </label>
                  ) : (
                    <span />
                  )}
                  {tenders.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" icon={<Trash2 className="size-4" />} onClick={() => setTenders(tenders.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="secondary" size="sm" icon={<Plus className="size-4" />} onClick={() => setTenders([...tenders, blankTender()])}>
                Add payment
              </Button>
            </div>
          </Card>

          <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <ErrorLine error={enter.error} />
          <div className="flex justify-end">
            <Button type="submit" loading={enter.isPending} disabled={!ready}>Enter sale</Button>
          </div>
        </form>
      )}

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Entered from outage logs" subtitle="Each slip once; when it happened and when it was entered, side by side" />
        </div>
        {entries.data && entries.data.data.length > 0 ? (
          <Table head={['Slip', 'Location', 'Happened', 'Entered', 'Sold by', 'Entered by', 'Sale', 'Amount', 'Payment']} minWidth={900}>
            {entries.data.data.map((r) => (
              <tr key={r.id}>
                <Td className="font-mono">{r.outage_log_ref}</Td>
                <Td>{r.branch_name}</Td>
                <Td>{dateTime(r.original_occurred_at, tz)}</Td>
                <Td>{dateTime(r.entered_at, tz)}</Td>
                <Td>{r.original_staff_name}</Td>
                <Td>{r.entered_by_name}</Td>
                <Td>{r.reference}</Td>
                <Td className="tabular-nums">{money(r.amount, c)}</Td>
                <Td><StatusBadge status={r.payment_status} /></Td>
              </tr>
            ))}
          </Table>
        ) : (
          <p className="px-5 pb-5 text-sm text-slate-500">No outage sales entered.</p>
        )}
      </Card>
    </div>
  );
}

function LineEditor({
  line,
  branchId,
  onChange,
  onRemove,
}: {
  line: Line;
  branchId: string;
  onChange: (next: Line) => void;
  onRemove?: (() => void) | undefined;
}): ReactNode {
  const item = usePharmacy<ListOf<MedicineListItem>>(
    '/medicines',
    { search: line.product?.name, branchId, limit: 5 },
    Boolean(line.product),
  );
  const med = useMemo(() => item.data?.data.find((m) => m.id === line.product?.id) ?? null, [item.data, line.product]);
  const stock = usePharmacy<ListOf<BatchRow>>('/batches', { productId: line.product?.id, branchId }, Boolean(line.product && med?.tracksBatches));
  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-5 sm:items-end">
      <div className="sm:col-span-2">
        <ProductPicker value={line.product} onChange={(product) => onChange({ ...line, product, unitId: '', batchId: '' })} />
      </div>
      <Select
        label="Unit"
        value={line.unitId}
        placeholder="Choose"
        disabled={!med}
        onChange={(e) => onChange({ ...line, unitId: e.target.value })}
        options={(med?.units ?? []).filter((u) => u.sellable).map((u) => ({ value: u.id, label: `${u.name} (${u.baseQuantity} ${med?.baseUnitName ?? ''})` }))}
      />
      <Input label="Quantity" inputMode="numeric" value={line.quantity} onChange={(e) => onChange({ ...line, quantity: e.target.value.replace(/\D/g, '') })} />
      <div className="flex items-end gap-2">
        {med?.tracksBatches ? (
          <Select
            label="Batch on the slip"
            value={line.batchId}
            placeholder="Earliest expiry then"
            onChange={(e) => onChange({ ...line, batchId: e.target.value })}
            options={(stock.data?.data ?? []).map((b) => ({ value: b.batchId, label: `${b.batchNumber} · exp ${b.expiryDate} · ${b.onHand} here` }))}
          />
        ) : (
          <span className="pb-2 text-xs text-slate-500">{med ? 'Not tracked by batch' : ''}</span>
        )}
        {onRemove && (
          <Button type="button" variant="ghost" size="sm" aria-label="Remove item" icon={<Trash2 className="size-4" />} onClick={onRemove} />
        )}
      </div>
    </div>
  );
}
